import axios from 'axios';

import {
  NewTimeEntry,
  StartTimerPayload,
  StopTimerPayload,
  TogglContext,
  TogglProject,
  TogglTimeEntry,
  TogglWorkspace,
} from '../types/toggl-api';

const TOGGL_API_BASE_URL = 'https://api.track.toggl.com/api/v9';

export function togglApi(apiToken: string) {
  const togglProxyServerUrl = import.meta.env.VITE_TOGGL_PROXY_URL as
    | string
    | undefined;

  const axiosInstance = axios.create({
    baseURL: togglProxyServerUrl
      ? `${togglProxyServerUrl}/api/v9`
      : TOGGL_API_BASE_URL,
    headers: {
      'Content-Type': 'application/json',
      Authorization: `Basic ${window.btoa(apiToken + ':api_token')}`,
    },
  });

  return {
    async getWorkspaces(): Promise<TogglWorkspace[]> {
      try {
        const { data } = await axiosInstance.get<TogglWorkspace[]>(
          '/me/workspaces'
        );
        return data;
      } catch (error) {
        console.error('Error while fetching user workspaces', error);
        return [];
      }
    },

    async getProjectPage(workspaceId: number, page: number) {
      try {
        const { data } = await axiosInstance.get<TogglProject[]>(
          `workspaces/${workspaceId}/projects?page=${page}&per_page=200&active=true`
        );
        return data;
      } catch (error) {
        console.error(
          `Error while fetching user page - ${page} of projects from workspace with id - ${workspaceId}`,
          error
        );
        return [];
      }
    },

    async getTimeEntries() {
      const { data } = await axiosInstance.get<TogglTimeEntry[]>(
        `me/time_entries`
      );
      return { time_entries: data };
    },

    async getTogglData() {
      const { data } = await axiosInstance.get<TogglContext>(
        `me?with_related_data=true`
      );
      return data;
    },

    async getWorkspaceProjects(workspaceId: number) {
      let page = 1;
      let hasMorePages = true;
      const allPages = [];

      while (hasMorePages) {
        const result = await this.getProjectPage(workspaceId, page);

        if (!result.length) {
          hasMorePages = false;
        } else {
          allPages.push(...result);
          page++;
        }
      }

      return allPages;
    },
    async getWorkspaceTags(workspaceId: number) {
      try {
        const { data } = await axiosInstance.get<TogglProject[]>(
          `workspaces/${workspaceId}/tags`
        );
        return data;
      } catch (error) {
        console.error(
          `Error while fetchingtags from workspace with id - ${workspaceId}`,
          error
        );
        return [];
      }
    },

    async getTimeEntriesForIssue(issueKey: string) {
      const { time_entries } = await this.getTimeEntries();
      return (
        time_entries
          ?.filter((timeEntry) =>
            timeEntry.description
              ?.toLowerCase()
              .includes(issueKey.toLowerCase())
          )
          ?.slice(0, 10) ?? []
      );
    },

    getCurrentTimer: async () => {
      const { data } = await axiosInstance.get<TogglTimeEntry>(
        '/me/time_entries/current'
      );
      return data;
    },

    createTimeEntry: async (newTimeEntry: StartTimerPayload) => {
      const { data } = await axiosInstance.post<TogglTimeEntry>(
        `/workspaces/${newTimeEntry.workspaceId}/time_entries`,
        mapToNewTimeEntry(newTimeEntry)
      );
      return data;
    },

    stopTimeEntry: async (timeEntryToStop: StopTimerPayload) => {
      const { data } = await axiosInstance.patch<TogglTimeEntry>(
        `/workspaces/${timeEntryToStop.workspaceId}/time_entries/${timeEntryToStop.timeEntryId}/stop`
      );
      return data;
    },
  };
}

function mapToNewTimeEntry({
  workspaceId,
  issue: {
    key,
    fields: { summary },
  },
  billable,
  projectId,
  taskId,
  tags,
}: StartTimerPayload) {
  const newTimeEntry: NewTimeEntry = {
    description: `${key} ${summary}`,
    billable,
    created_with: 'JIRA',
    start: new Date(Date.now()).toISOString(),
    duration: -1,
    workspace_id: workspaceId,
  };

  if (projectId) {
    newTimeEntry.project_id = projectId;
  }
  if (taskId) {
    newTimeEntry.task_id = taskId;
  }
  if (tags) {
    newTimeEntry.tags = tags;
  }
  return newTimeEntry;
}
